import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";

import { getConfigWithToken } from "../config/config";
import { getDisplayRole, resolveStaffStatus, STAFF_ROLES } from "../utils/staffRoles";
import { getUsers } from "../config/url";
import classes from "./Staff.module.css";
import InviteStaffDrawer from "../components/InviteStaffDrawer";

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const SEARCH_DEBOUNCE_MS = 500;

const ROLE_OPTIONS = STAFF_ROLES;

const STATUS_OPTIONS = ["Active", "Inactive", "Pending"];

const ROLE_CLASS = {
	Admin: classes.roleAdmin,
	Provider: classes.roleStaff,
	QA: classes.roleCompliance,
	Supervisor: classes.roleSupervisor,
};

const STATUS_CLASS = {
	Active: classes.statusActive,
	Inactive: classes.statusInactive,
	Pending: classes.statusPending,
};

const EMPTY_SUMMARY = { active: 0, admins: 0, pending: 0, staff: 0, total: 0 };

const formatLastLogin = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";

	const now = new Date();
	const sameDay =
		date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();

	const yesterday = new Date(now);
	yesterday.setDate(now.getDate() - 1);
	const isYesterday =
		date.getFullYear() === yesterday.getFullYear() &&
		date.getMonth() === yesterday.getMonth() &&
		date.getDate() === yesterday.getDate();

	const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

	if (sameDay) return `Today, ${time}`;
	if (isYesterday) return `Yesterday, ${time}`;
	return date.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
};

const getDisplayName = (staffItem) =>
	[staffItem?.firstname, staffItem?.lastname].filter(Boolean).join(" ").trim() || staffItem?.email || "-";

const Staff = () => {
	const navigate = useNavigate();

	const currentUser = useSelector((state) => state?.user);
	const token = currentUser?.user?.token || localStorage.getItem("user_id");
	const organizationId =
		currentUser?.user?.organizationId ||
		localStorage.getItem("organizationId") ||
		localStorage.getItem("businessId");

	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [hasError, setHasError] = useState(false);
	const [isInviteOpen, setIsInviteOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [roleFilter, setRoleFilter] = useState("");
	const [search, setSearch] = useState("");
	const [staffList, setStaffList] = useState([]);
	const [statusFilter, setStatusFilter] = useState("");
	const [summary, setSummary] = useState(EMPTY_SUMMARY);
	const [totalItems, setTotalItems] = useState(0);
	const [totalPages, setTotalPages] = useState(1);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
				...(organizationId ? { "x-organization-id": organizationId } : {}),
			},
		}),
		[organizationId, token],
	);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [search]);

	const fetchStaff = useCallback(async () => {
		setIsLoading(true);
		setHasError(false);
		try {
			const response = await axios.get(getUsers, {
				...requestConfig,
				params: {
					limit: pageSize,
					page,
					...(organizationId ? { organizationId } : {}),
					...(debouncedSearch ? { search: debouncedSearch } : {}),
					...(roleFilter ? { role: roleFilter } : {}),
					...(statusFilter ? { status: statusFilter } : {}),
				},
			});
			const nextSummary = response?.data?.summary || EMPTY_SUMMARY;
			const pagination = response?.data?.pagination || {};
			const rows = response?.data?.data;

			setStaffList(Array.isArray(rows) ? rows : []);
			setSummary({
				active: nextSummary.active || 0,
				admins: nextSummary.admins || 0,
				pending: nextSummary.pending || 0,
				staff: nextSummary.staff || 0,
				total: nextSummary.total || 0,
			});
			setTotalItems(pagination.total || 0);
			setTotalPages(Math.max(1, pagination.totalPages || 1));
		} catch {
			setHasError(true);
			setStaffList([]);
			setSummary(EMPTY_SUMMARY);
			setTotalItems(0);
			setTotalPages(1);
		} finally {
			setIsLoading(false);
		}
	}, [debouncedSearch, organizationId, page, pageSize, requestConfig, roleFilter, statusFilter]);

	useEffect(() => {
		fetchStaff();
	}, [fetchStaff]);

	useEffect(() => {
		setPage((current) => (current === 1 ? current : 1));
	}, [debouncedSearch, pageSize, roleFilter, statusFilter]);

	const hasFilters = Boolean(debouncedSearch || roleFilter || statusFilter);
	const isEmptyOrg = !isLoading && !hasError && summary.total === 0 && !hasFilters;
	const isNoResults = !isLoading && !hasError && totalItems === 0 && (hasFilters || summary.total > 0);
	const currentPage = Math.min(page, totalPages);

	return (
		<div className={classes.page}>
			<div className={classes.headerRow}>
				<h1 className={classes.title}>Staff</h1>
				<p className={classes.subtitle}>
					Everyone with access to TeleTrace - accounts, roles, and clinical assignments.
				</p>
			</div>

			<div className={classes.summaryGrid}>
				{isLoading
					? Array.from({ length: 5 }).map((_, index) => (
							<div className={classes.skeletonCard} key={`skeleton-${index}`} />
						))
					: [
							{ label: "Total Staff", value: summary.total },
							{ label: "Active", value: summary.active },
							{ label: "Staff", value: summary.staff },
							{ label: "Admins", value: summary.admins },
							{ label: "Pending", value: summary.pending },
						].map((card) => (
							<div className={classes.summaryCard} key={card.label}>
								<span className={classes.summaryLabel}>{card.label}</span>
								<span className={classes.summaryValue}>{card.value}</span>
							</div>
						))}
			</div>

			<div className={classes.toolbar}>
				<label className={classes.searchWrap}>
					<Search size={18} strokeWidth={1.75} />
					<input
						className={classes.searchInput}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Search by name or email..."
						type="search"
						value={search}
					/>
				</label>
				<select
					aria-label="Role"
					className={classes.filterSelect}
					onChange={(event) => setRoleFilter(event.target.value)}
					value={roleFilter}>
					<option value="">Role</option>
					{ROLE_OPTIONS.map((role) => (
						<option key={role} value={role}>
							{role}
						</option>
					))}
				</select>
				<select
					aria-label="Status"
					className={classes.filterSelect}
					onChange={(event) => setStatusFilter(event.target.value)}
					value={statusFilter}>
					<option value="">Status</option>
					{STATUS_OPTIONS.map((status) => (
						<option key={status} value={status}>
							{status}
						</option>
					))}
				</select>
				<button className={classes.inviteButton} onClick={() => setIsInviteOpen(true)} type="button">
					<Plus size={18} strokeWidth={2} />
					Invite Staff
				</button>
			</div>

			<div className={classes.tableCard}>
				{isLoading ? (
					<div className={classes.stateBlock}>
						<p className={classes.stateText}>Loading staff...</p>
					</div>
				) : hasError ? (
					<div className={classes.stateBlock}>
						<p className={classes.stateTitle}>Unable to load staff</p>
						<p className={classes.stateText}>Something went wrong. Please try again.</p>
						<button className={classes.retryButton} onClick={fetchStaff} type="button">
							Try Again
						</button>
					</div>
				) : isEmptyOrg ? (
					<div className={classes.stateBlock}>
						<p className={classes.stateTitle}>No staff yet</p>
						<p className={classes.stateText}>
							Invite staff to give them access to TeleTrace for this organization.
						</p>
					</div>
				) : isNoResults ? (
					<div className={classes.stateBlock}>
						<p className={classes.stateTitle}>No matching staff</p>
						<p className={classes.stateText}>
							{hasFilters ? "Try adjusting your search or filters." : "No staff found."}
						</p>
					</div>
				) : (
					<>
						<table className={classes.table}>
							<thead>
								<tr>
									<th>Name</th>
									<th>Email</th>
									<th>Role</th>
									<th>Status</th>
									<th>Last Login</th>
								</tr>
							</thead>
							<tbody>
								{staffList.map((row) => {
									const role = getDisplayRole(row);
									const status = resolveStaffStatus(row);
									return (
										<tr key={row._id} onClick={() => navigate(`/staff/${row._id}`)}>
											<td className={classes.nameCell}>{getDisplayName(row)}</td>
											<td>{row.email || "-"}</td>
											<td>
												<div className={classes.roleList}>
													{role ? (
														<span
															className={`${classes.roleBadge} ${ROLE_CLASS[role] || classes.roleDefault}`}>
															{role}
														</span>
													) : (
														<span className={`${classes.roleBadge} ${classes.roleDefault}`}>-</span>
													)}
												</div>
											</td>
											<td>
												<span className={`${classes.statusBadge} ${STATUS_CLASS[status] || ""}`}>
													{status}
												</span>
											</td>
											<td>{formatLastLogin(row.lastLogin)}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
						<div className={classes.footer}>
							<label className={classes.rowsPerPage}>
								Rows per page
								<select
									className={classes.rowsSelect}
									onChange={(event) => setPageSize(Number(event.target.value))}
									value={pageSize}>
									{PAGE_SIZE_OPTIONS.map((size) => (
										<option key={size} value={size}>
											{size}
										</option>
									))}
								</select>
							</label>
							<div className={classes.pageControls}>
								<span className={classes.pageLabel}>
									Page {currentPage} of {totalPages}
								</span>
								<button
									aria-label="Previous page"
									className={classes.pageButton}
									disabled={currentPage <= 1}
									onClick={() => setPage((current) => Math.max(1, current - 1))}
									type="button">
									<ChevronLeft size={16} />
								</button>
								<button
									aria-label="Next page"
									className={classes.pageButton}
									disabled={currentPage >= totalPages}
									onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
									type="button">
									<ChevronRight size={16} />
								</button>
							</div>
						</div>
					</>
				)}
			</div>

			<InviteStaffDrawer
				isOpen={isInviteOpen}
				onClose={() => setIsInviteOpen(false)}
				onSuccess={fetchStaff}
				onViewProfile={(userId) => navigate(`/staff/${userId}`)}
			/>
		</div>
	);
};

export default Staff;
